import React, { useState, useCallback, useMemo } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Collapse,
  IconButton,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SettingsIcon from "@mui/icons-material/Settings";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import DescriptionIcon from "@mui/icons-material/Description";
import { Link, useLocation } from "react-router-dom";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useSelector } from "react-redux";
import "./side-bar.css";
import CloseIcon from "@mui/icons-material/Close";

const expandedWidth = 255; // Full width for larger screens

const Sidebar = ({ setIsSidebarCollapsed }) => {
  const user = useSelector((state) => state.auth.user);
  const memoizedUser = useMemo(() => user, [user]);
  const [open, setOpen] = useState(false);
  const [openForms, setOpenForms] = useState(false);
  const location = useLocation(); // Get the current route

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleFormToggle = useCallback(() => {
    setOpenForms((prev) => !prev);
  }, []);

  const isManagerOrAdmin = memoizedUser.roleid === 2 || memoizedUser.roleid === 3;

  const isActive = (path) => location.pathname === path; // Check if the route matches

  return (
    <Drawer
      variant='permanent'
      anchor='left'
      sx={{
        padding: "5px",
        width: expandedWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          margin: "10px",
          padding: "5px",
          width: expandedWidth,
          boxSizing: "border-box",
          backgroundColor: "rgba(255, 255, 255, 0.98)", // Slight transparency
        },
      }}
    >
      <Box p={2}>
        {window.innerWidth < 1024 && (
          <>
            <IconButton onClick={() => setIsSidebarCollapsed?.(true)}>
              <CloseIcon />
            </IconButton>
            <br />
          </>
        )}

        <img src='/images/logo.png' alt='Active Care Logo' className='sidebar-logo' />
      </Box>
      <List>
        <Typography variant='body2' gutterBottom ml={2}>
          Overview
        </Typography>
        <ListItem
          button
          component={Link}
          to='/dashboard'
          onClick={() => window.innerWidth < 1024 && setIsSidebarCollapsed?.(true)}
          sx={{
            backgroundColor: isActive("/dashboard") ? "#e3f2fd" : "transparent",
            color: isActive("/dashboard") ? "#1976d2" : "inherit",
          }}
        >
          <ListItemIcon>
            <DashboardIcon fontSize='small' sx={{ color: isActive("/dashboard") ? "#1976d2" : "inherit" }} />
          </ListItemIcon>
          <ListItemText primary='Dashboard' className='sidebar-text' />
        </ListItem>
        <ListItem
          button
          component={Link}
          to='/daily-logs'
          onClick={() => window.innerWidth < 1024 && setIsSidebarCollapsed?.(true)}
          sx={{
            backgroundColor: isActive("/daily-logs") ? "#e3f2fd" : "transparent",
            color: isActive("/daily-logs") ? "#1976d2" : "inherit",
          }}
        >
          <ListItemIcon>
            <VerifiedUserIcon fontSize='small' sx={{ color: isActive("/daily-logs") ? "#1976d2" : "inherit" }} />
          </ListItemIcon>
          <ListItemText primary='Daily Logs' className='sidebar-text' />
        </ListItem>
        {isManagerOrAdmin && (
          <List>
            {/* Parent Task Menu */}
            <ListItem button onClick={handleToggle}>
              <ListItemIcon>
                <DescriptionIcon fontSize='small' />
              </ListItemIcon>
              <ListItemText primary='Tasks' className='sidebar-text' />
              {open ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            {/* Collapsible Sub-menu */}
            <Collapse in={open} timeout='auto' unmountOnExit>
              <List component='div' disablePadding>
                <ListItem
                  button
                  component={Link}
                  to='/manager-all-tasks'
                  onClick={() => window.innerWidth < 1024 && setIsSidebarCollapsed?.(true)}
                  sx={{
                    backgroundColor: isActive("/manager-all-tasks") ? "#e3f2fd" : "transparent",
                    color: isActive("/manager-all-tasks") ? "#1976d2" : "inherit",
                  }}
                >
                  <ListItemIcon>
                    <FiberManualRecordIcon
                      fontSize='small'
                      style={{
                        fontSize: "8px",
                        color: isActive("/manager-all-tasks") ? "#1976d2" : "inherit",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText primary=' All Tasks' />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to='/manager-task-assign'
                  onClick={() => window.innerWidth < 1024 && setIsSidebarCollapsed?.(true)}
                  sx={{
                    backgroundColor: isActive("/manager-task-assign") ? "#e3f2fd" : "transparent",
                    color: isActive("/manager-task-assign") ? "#1976d2" : "inherit",
                  }}
                >
                  <ListItemIcon>
                    <FiberManualRecordIcon
                      fontSize='small'
                      style={{
                        fontSize: "8px",
                        color: isActive("/manager-task-assign") ? "#1976d2" : "inherit",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText primary=' Manage Tasks' />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to='/manager-task-incomplete'
                  onClick={() => window.innerWidth < 1024 && setIsSidebarCollapsed?.(true)}
                  sx={{
                    backgroundColor: isActive("/manager-task-incomplete") ? "#e3f2fd" : "transparent",
                    color: isActive("/manager-task-incomplete") ? "#1976d2" : "inherit",
                  }}
                >
                  <ListItemIcon>
                    <FiberManualRecordIcon
                      fontSize='small'
                      style={{
                        fontSize: "8px",
                        color: isActive("/manager-task-incomplete") ? "#1976d2" : "inherit",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText primary=' Manage Tasks History' />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to='/manager-task-complete'
                  onClick={() => window.innerWidth < 1024 && setIsSidebarCollapsed?.(true)}
                  sx={{
                    backgroundColor: isActive("/manager-task-complete") ? "#e3f2fd" : "transparent",
                    color: isActive("/manager-task-complete") ? "#1976d2" : "inherit",
                  }}
                >
                  <ListItemIcon>
                    <FiberManualRecordIcon
                      fontSize='small'
                      style={{
                        fontSize: "8px",
                        color: isActive("/manager-task-complete") ? "#1976d2" : "inherit",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText primary=' Complete Tasks' />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to='/manager-task-create'
                  onClick={() => window.innerWidth < 1024 && setIsSidebarCollapsed?.(true)}
                  sx={{
                    backgroundColor: isActive("/manager-task-create") ? "#e3f2fd" : "transparent",
                    color: isActive("/manager-task-create") ? "#1976d2" : "inherit",
                  }}
                >
                  <ListItemIcon>
                    <FiberManualRecordIcon
                      fontSize='small'
                      style={{
                        fontSize: "8px",
                        color: isActive("/manager-task-create") ? "#1976d2" : "inherit",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText sx={{ fontSize: "8px" }} primary='Create Tasks' />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to='/manager-task-form'
                  onClick={() => window.innerWidth < 1024 && setIsSidebarCollapsed?.(true)}
                  sx={{
                    backgroundColor: isActive("/manager-task-form") ? "#e3f2fd" : "transparent",
                    color: isActive("/manager-task-form") ? "#1976d2" : "inherit",
                  }}
                >
                  <ListItemIcon>
                    <FiberManualRecordIcon
                      fontSize='small'
                      style={{
                        fontSize: "8px",
                        color: isActive("/manager-task-form") ? "#1976d2" : "inherit",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText sx={{ fontSize: "8px" }} primary='Task Form' />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to='/task-history'
                  onClick={() => window.innerWidth < 1024 && setIsSidebarCollapsed?.(true)}
                  sx={{
                    backgroundColor: isActive("/task-history") ? "#e3f2fd" : "transparent",
                    color: isActive("/task-history") ? "#1976d2" : "inherit",
                  }}
                >
                  <ListItemIcon>
                    <FiberManualRecordIcon
                      fontSize='small'
                      style={{
                        fontSize: "8px",
                        color: isActive("/task-history") ? "#1976d2" : "inherit",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText primary='Task History' />
                </ListItem>
              </List>
            </Collapse>
          </List>
        )}

        {isManagerOrAdmin && (
          <List>
            {/* Parent Task Menu */}
            <ListItem button onClick={handleFormToggle}>
              <ListItemIcon>
                <DescriptionIcon fontSize='small' />
              </ListItemIcon>
              <ListItemText primary='Forms' className='sidebar-text' />
              {openForms ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            {/* Collapsible Sub-menu */}
            <Collapse in={openForms} timeout='auto' unmountOnExit>
              <List component='div' disablePadding>
                <ListItem
                  button
                  component={Link}
                  to='/incident-management-view'
                  onClick={() => window.innerWidth < 1024 && setIsSidebarCollapsed?.(true)}
                  sx={{
                    backgroundColor: isActive("/incident-management-view") ? "#e3f2fd" : "transparent",
                    color: isActive("/incident-management-view") ? "#1976d2" : "inherit",
                  }}
                >
                  <ListItemIcon>
                    <FiberManualRecordIcon
                      fontSize='small'
                      style={{
                        fontSize: "8px",
                        color: isActive("/incident-management-view") ? "#1976d2" : "inherit",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText primary=' Incident Management' />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to='/safe-guarding-view'
                  onClick={() => window.innerWidth < 1024 && setIsSidebarCollapsed?.(true)}
                  sx={{
                    backgroundColor: isActive("/safe-guarding-view") ? "#e3f2fd" : "transparent",
                    color: isActive("/safe-guarding-view") ? "#1976d2" : "inherit",
                  }}
                >
                  <ListItemIcon>
                    <FiberManualRecordIcon
                      fontSize='small'
                      style={{
                        fontSize: "8px",
                        color: isActive("/safe-guarding-view") ? "#1976d2" : "inherit",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText primary='Safeguard' />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to='/cause-concern-view'
                  onClick={() => window.innerWidth < 1024 && setIsSidebarCollapsed?.(true)}
                  sx={{
                    backgroundColor: isActive("/cause-concern-view") ? "#e3f2fd" : "transparent",
                    color: isActive("/cause-concern-view") ? "#1976d2" : "inherit",
                  }}
                >
                  <ListItemIcon>
                    <FiberManualRecordIcon
                      fontSize='small'
                      style={{
                        fontSize: "8px",
                        color: isActive("/cause-concern-view") ? "#1976d2" : "inherit",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText sx={{ fontSize: "8px" }} primary='Cause For Concerns' />
                </ListItem>
              </List>
            </Collapse>
          </List>
        )}
        <Typography variant='body2' gutterBottom ml={2} mt={user.roleid === 4 ? 35 : 25}>
          Settings
        </Typography>
        <ListItem button>
          <ListItemIcon>
            <SettingsIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText primary='Settings' className='sidebar-text' />
        </ListItem>
        <ListItem
          button
          component={Link}
          to='/logout'
          onClick={() => window.innerWidth < 1024 && setIsSidebarCollapsed?.(true)}
          sx={{
            backgroundColor: isActive("/logout") ? "#e3f2fd" : "transparent",
            color: isActive("/logout") ? "#1976d2" : "inherit",
          }}
        >
          <ListItemIcon>
            <ExitToAppIcon
              fontSize='small'
              style={{
                fontSize: "20px",
                color: isActive("/logout") ? "#1976d2" : "inherit",
              }}
            />
          </ListItemIcon>
          <ListItemText primary='Log Out' className='sidebar-text' />
        </ListItem>
      </List>
    </Drawer>
  );
};

export default Sidebar;
